import React, { ReactElement, useCallback, useMemo } from 'react';
import { alpha } from '@mui/material';
import BigNumber from 'bignumber.js';
import { makeStyles } from 'tss-react/mui';

import { HUNDRED, ONE, ZERO } from 'modules/common';
import {
  globalTranslation,
  mergeTranslations,
  useTranslation,
} from 'modules/i18n';
import { ArrowRightIcon, IotaTokenIcon, StIotaTokenIcon } from 'modules/icons';

import { useGetStatsQuery } from '../../actions/getStats';
import { translation } from './translation';

const mergedTranslation = mergeTranslations(globalTranslation, translation);

interface IDetailsProps {
  isStakeTab: boolean;
  amount: BigNumber;
  fee?: BigNumber;
  staked: BigNumber;
}

export function Details({
  isStakeTab,
  amount,
  fee,
  staked,
}: IDetailsProps): ReactElement {
  const { classes, cx } = useStyles();
  const { t, keys } = useTranslation(mergedTranslation);

  const { data } = useGetStatsQuery();

  const rate = data && !data.rate.isZero() ? data.rate : ONE;
  const apy = data?.apyTotal;

  const getAmount = useMemo(() => {
    if (!rate || rate.isZero()) return ZERO;

    return isStakeTab ? amount.multipliedBy(rate) : amount.div(rate);
  }, [amount, isStakeTab, rate]);

  const stakedIota = useMemo(() => {
    if (!rate || rate.isZero()) return ZERO;

    return staked.div(rate);
  }, [staked, rate]);

  const newYearlyAmount = useMemo(() => {
    const newAmount = isStakeTab ? staked.plus(amount) : staked.minus(amount);
    return newAmount.isLessThan(ZERO) ? ZERO : newAmount;
  }, [amount, isStakeTab, staked]);

  const renderFee = useCallback(() => {
    return (
      <div className={classes.item}>
        <div className={classes.title}>{t(keys.transactionFee)}</div>

        <div className={classes.value}>
          {fee
            ? t(keys.unit.tokenValue, {
                token: t(keys.tokens.native),
                value: fee.toFormat(),
              })
            : t(keys.common.none)}
        </div>
      </div>
    );
  }, [classes, fee, keys, t]);

  return (
    <div className={classes.root}>
      <div className={cx(classes.item, classes.first)}>
        <div className={classes.title}>{t(keys.youWillGet)}</div>

        <div className={classes.value}>
          {isStakeTab ? (
            <StIotaTokenIcon className={classes.image} />
          ) : (
            <IotaTokenIcon className={classes.image} />
          )}

          {rate
            ? t(keys.unit.tokenValue, {
                token: isStakeTab
                  ? t(keys.tokens.staked)
                  : t(keys.tokens.native),
                value: getAmount.toFixed(4),
              })
            : t(keys.common.none)}
        </div>
      </div>

      <div className={classes.item}>
        <div className={classes.title}>{t(keys.exchangeRate)}</div>

        <div className={classes.value}>
          {rate
            ? t(keys.rate, {
                nativeToken: isStakeTab
                  ? t(keys.tokens.native)
                  : t(keys.tokens.staked),
                value: isStakeTab ? rate.toFixed(4) : ONE.div(rate).toFixed(4),
                token: isStakeTab
                  ? t(keys.tokens.staked)
                  : t(keys.tokens.native),
              })
            : t(keys.common.none)}
        </div>
      </div>

      {renderFee()}

      <div className={classes.item}>
        <div className={classes.title}>{t(keys.yearly)}</div>

        {apy ? (
          <div className={classes.value}>
            {t(keys.unit.tokenValue, {
              token: t(keys.tokens.native),
              value: stakedIota.multipliedBy(apy).div(HUNDRED).toFixed(2),
            })}

            {!amount.isZero() && (
              <div
                className={cx(
                  classes.newValue,
                  isStakeTab && classes.newValueStake,
                )}
              >
                <ArrowRightIcon />

                {t(keys.unit.tokenValue, {
                  token: t(keys.tokens.native),
                  value: newYearlyAmount
                    .multipliedBy(apy)
                    .div(HUNDRED)
                    .toFixed(2),
                })}
              </div>
            )}
          </div>
        ) : (
          t(keys.common.none)
        )}
      </div>
    </div>
  );
}

const useStyles = makeStyles()(theme => ({
  root: {
    marginTop: theme.spacing(9.5),

    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(6),
    },
  },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(4),
    fontSize: 14,
    fontWeight: 400,
  },
  first: {
    minHeight: 33,
    paddingBottom: theme.spacing(4),
    borderBottom: `1px solid ${theme.palette.background.default}`,
    marginTop: theme.spacing(0),
  },
  title: {
    color: alpha(theme.palette.secondary.contrastText, 0.5),
  },
  value: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  image: {
    width: 16,
    height: 16,
  },
  newValue: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    color: theme.palette.error.main,
  },
  newValueStake: {
    color: theme.palette.primary.main,
  },
}));
